import { useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { ScanningStatus } from "../models/benchly.models";
import { useCitationStore } from "../store/CitationStore";

export interface UseScanningResult {
  status: ScanningStatus;
  progress: number;
  statusMessage: string;
  foundCitations: number;
  errorMessage: string;
}

/**
 * Thin hook that:
 * 1. Triggers startScanning() on mount (if idle)
 * 2. Reads state from CitationStore (survives unmount)
 * 3. Handles navigation based on status transitions
 */
export const useScanning = (): UseScanningResult => {
  const navigate = useNavigate();
  const {
    scanStatus: status,
    scanProgress: progress,
    scanStatusMessage: statusMessage,
    foundCitations,
    scanErrorMessage: errorMessage,
    startScanning,
  } = useCitationStore();

  // Trigger scan on mount if idle
  useEffect(() => {
    if (status === ScanningStatus.Idle) {
      startScanning();
    }
  }, [status, startScanning]);

  // Navigate based on status
  useEffect(() => {
    if (status === ScanningStatus.Complete) {
      // Exact search is done — navigate to executive summary
      const timer = setTimeout(() => {
        navigate("/executive-summary");
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (status === ScanningStatus.NoCitations) {
      const timer = setTimeout(() => {
        navigate("/");
      }, 3000);
      return () => clearTimeout(timer);
    }

    if (status === ScanningStatus.Error) {
      const timer = setTimeout(() => {
        navigate("/");
      }, 5000);
      return () => clearTimeout(timer);
    }

    return () => {};
  }, [status, navigate]);

  return {
    status,
    progress,
    statusMessage,
    foundCitations,
    errorMessage,
  };
};
